import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { Grid, Menu as MenuAntD } from 'antd';
import { ItemType, MenuItemType } from "antd/lib/menu/hooks/useItems";

import { TypeMenu } from "@/Models/Menu";

import * as Icones from '@ant-design/icons';
import { obterProfundidadeDasChaves, comporMenuHierarquico } from "@/Utils/Menu";
import { useMenusDisponiveis, useMenuSelecionado } from "@/Contexts/NavegacaoContext";
import { ItemDoMenu } from "./menu-types";

const obterIcone = (nomeDoIcone?: string) => {
    if (nomeDoIcone == null || nomeDoIcone == "") return undefined;
    const Icone = (Icones as any)[nomeDoIcone];
    return Icone ? <Icone /> : undefined;
};

export function Menu(props: { itemsAntes?: ItemType<MenuItemType>[]; colapsado?: boolean }): JSX.Element {
    const breakpoint = Grid.useBreakpoint();
    const _location = useLocation();
    const { setMenuSelecionado } = useMenuSelecionado();
    const menus = useMenusDisponiveis();

    const itensDoMenu = useMemo(() => {
        if (menus == null) return;
        const menuHierarquico = comporMenuHierarquico(menus);

        const f = (menu: TypeMenu): ItemType<MenuItemType> => {
            const possuiFilhos = menu.menusFilhos && menu.menusFilhos.length > 0;
            let key = menu.codigo.toString();
            const rota = NovasRotas[menu.codigo as ItemDoMenu]?.url ?? "";
            // const rota = menu.rota ?? "";

            return {
                key,
                label: possuiFilhos ? menu.nome : <Link to={rota}>{menu.nome}</Link>,
                icon: obterIcone(menu.icone),
                children: possuiFilhos ? menu.menusFilhos!.map((m) => f(m)) : undefined,
                style: {
                    whiteSpace: "normal",
                    height: !props.colapsado ? "fit-content" : undefined,
                },
            };
        };

        return [...(props.itemsAntes ?? []), ...menuHierarquico.map((v) => f(v))];
    }, [menus, props.colapsado, props.itemsAntes]);

    const rotasDoCaminhoAtual = useMemo(() => {
        const parts = _location.pathname.split("/").filter((part) => part !== "");
        const result: string[] = [];

        if (parts.length === 0) return ["/"];

        let currentPath = "";

        parts.forEach((part) => {
            currentPath += `/${part}`;
            result.push(currentPath);
        });

        return result;
    }, [_location]);

    //#region controle de um menu aberto por vez

    const [chavesDosMenusAbertos, setChaveDosMenusAbertos] = useState<string[]>(breakpoint.lg ? rotasDoCaminhoAtual : []);

    const aoAbrirMenu = (novasChavesAbertas: string[]) => {
        const profundidadeDasChaves = obterProfundidadeDasChaves(itensDoMenu);

        const chaveSendoAberta = novasChavesAbertas.find((key) => chavesDosMenusAbertos.indexOf(key) === -1);
        if (chaveSendoAberta !== undefined) {
            const indiceDaOutraChaveDoMesmoNivelAberta = novasChavesAbertas
                .filter((key) => key !== chaveSendoAberta)
                .findIndex((key) => profundidadeDasChaves[key] === profundidadeDasChaves[chaveSendoAberta]);

            setChaveDosMenusAbertos(
                novasChavesAbertas
                    .filter((_, index) => index !== indiceDaOutraChaveDoMesmoNivelAberta)
                    .filter((key) => profundidadeDasChaves[key] <= profundidadeDasChaves[chaveSendoAberta])
            );
        } else {
            setChaveDosMenusAbertos(novasChavesAbertas);
        }
    };

    //#endregion

    const salvarMenuSelecionado = (key: string) => {
        const _menuSelecionado = menus?.find((m) => m.codigo === key);
        if (_menuSelecionado !== undefined) {
            setMenuSelecionado(_menuSelecionado);
        } else {
            setMenuSelecionado(null);
        }
    };

    useEffect(() => {
        const arr = [...rotasDoCaminhoAtual].reverse();
        for (const arrItem of arr) {
            /* Encontra a primeira chave válida */
            Object.entries(NovasRotas).forEach(([key, value]) => {
                if (value.url === arrItem) {
                    salvarMenuSelecionado(key);
                    return;
                }
            });
        }
    }, [rotasDoCaminhoAtual, menus]);

    return (
        <>
            <React.Fragment>
                <MenuAntD
                    mode="inline"
                    onSelect={(m) => salvarMenuSelecionado(m.key)}
                    defaultSelectedKeys={rotasDoCaminhoAtual}
                    openKeys={chavesDosMenusAbertos}
                    onOpenChange={aoAbrirMenu}
                    style={{ borderRight: 0 }}
                    items={itensDoMenu}
                    theme="dark"
                />
            </React.Fragment>
        </>
    );
}

export const NovasRotas: Record<ItemDoMenu, { url: string }> = {
    "_tela.home": { url: "/" },

    "_agrupador.viagem": {
        url: "/viagens/"
    },
    "_tela.viagem.cadastros.viagem": {
        url: "/viagens/viagens",
    },
    "_tela.viagem.inscricoesRecentes": {
        url: "/viagens/inscricoes-recentes",
    },
    "_tela.viagem.cadastro.desconto": {
        url: "/viagens/descontos",
    },
    "_tela.viagem.cadastro.adv": {
        url: "/viagens/adv",
    },
    "_tela.pessoa.cadastros.aluno": {
        url: "/viagens/alunos",
    },
    "_tela.pessoa.cadastros.responsavel": {
        url: "/viagens/responsaveis",
    },
    "_tela.viagem.cadastros.tipoDeViagem": {
        url: "/viagens/tipos-de-viagem",
    },
    "_tela.viagem.cadastros.destino": {
        url: "/viagens/destinos",
    },

    "_agrupador.financeiro": {
        url: "/financeiro/",
    },
    "_tela.financeiro.movimentacao": {
        url: "/financeiro/movimentacoes",
    },
    "_tela.financeiro.titulo.aPagar": {
        url: "/financeiro/titulos/a-pagar",
    },
    "_tela.financeiro.titulo.aReceber": {
        url: "/financeiro/titulos/a-receber",
    },
    "_tela.financeiro.conciliacaoAuttar": {
        url: "/financeiro/conciliacao-auttar",
    },
    "_tela.financeiro.conciliacaoGetNet": {
        url: "/financeiro/conciliacao-getnet",
    },
    "_tela.financeiro.conciliacaoPagBank": {
        url: "/financeiro/conciliacao-pagbank/vendas"
    },
    "_tela.financeiro.conciliacaoPagBank.Cancelamentos": {
        url: "/financeiro/conciliacao-pagbank/cancelamentos"
    },
    "_tela.financeiro.conciliacaoPagBank.Pagamentos": {
        url: "/financeiro/conciliacao-pagbank/pagamentos"
    },
    "_tela.faturamento.nota": {
        url: "/financeiro/notas",
    },
    "_tela.faturamento.boletos": {
        url: "/financeiro/boletos",
    },

    "_agrupador.relatorios": {
        url: "/relatorios/",
    },
    "_tela.financeiro.relatorios.extratoBancario": {
        url: "/relatorios/extrato-bancario",
    },
    "_tela.financeiro.relatorios.contasXMovimentacoes": {
        url: "/relatorios/contas-x-movimentacoes",
    },
    "_tela.financeiro.relatorios.fluxoRateio": {
        url: "/relatorios/fluxo-rateio",
    },
    "_tela.relatorios.pessoas-bloqueadas": {
        url: "/relatorios/pessoas-bloqueadas",
    },
    "_tela.financeiro.relatorios.titulosAcumulados": {
        url: "/relatorios/titulos-acumulados",
    },
    "_tela.financeiro.relatorios.visaoGeralMovimentacoes": {
        url: "/relatorios/visao-geral-movimentacoes",
    },
    "_tela.financeiro.relatorios.visaoMensal": {
        url: "/relatorios/visao-mensal",
    },

    "_agrupador.cadastrosBasicos": {
        url: "/cadastros/",
    },

    "_agrupador.cadastrosBasicos.empresas": {
        url: "/cadastros/empresas/",
    },
    "_tela.pessoa.cadastros.escola": {
        url: "/cadastros/empresas/escola",
    },
    "_tela.pessoa.cadastros.fornecedor": {
        url: "/cadastros/empresas/fornecedor",
    },
    "_tela.pessoa.cadastros.empresa": {
        url: "/cadastros/empresas/empresa",
    },

    "_agrupador.cadastrosBasicos.bancos": {
        url: "/cadastros/bancos/",
    },
    "_tela.financeiro.cadastros.banco": {
        url: "/cadastros/bancos/banco",
    },
    "_tela.financeiro.cadastros.agencia": {
        url: "/cadastros/bancos/agencia",
    },
    "_tela.financeiro.cadastros.conta": {
        url: "/cadastros/bancos/conta",
    },

    "_agrupador.cadastrosBasicos.contabilidade": {
        url: "/cadastros/contabilidade/",
    },
    "_tela.financeiro.cadastros.centroCusto": {
        url: "/cadastros/contabilidade/centro-de-custo",
    },
    "_tela.financeiro.cadastros.planoContas": {
        url: "/cadastros/contabilidade/plano-de-contas",
    },

    "_tela.usuario.cadastros.usuario": {
        url: "/cadastros/usuarios",
    },

    "_agrupador.configuracoes": {
        url: "/configuracoes/",
    },
    "_agrupador.configuracoes.fichaDeSaude": {
        url: "/configuracoes/ficha-de-saude/",
    },
    "_tela.pessoa.fichaDeSaude.alergia": {
        url: "/configuracoes/ficha-de-saude/alergia",
    },
    "_tela.pessoa.fichaDeSaude.enfermidades": {
        url: "/configuracoes/ficha-de-saude/enfermidades",
    },
    "_tela.pessoa.fichaDeSaude.vacina": {
        url: "/configuracoes/ficha-de-saude/vacina",
    },
    "_tela.faturamento.certificado": {
        url: "/configuracoes/certificados",
    },
    "_tela.faturamento.servico": {
        url: "/configuracoes/servicos",
    },
    "_tela.usuario.cadastros.perfil": {
        url: "/configuracoes/perfil",
    },
    "_tela.geral.data.feriados": {
        url: "/Geral/Feriados"
    }
};